import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Crown, Award } from "lucide-react";

/**
 * Simplified affiliate settings — rank-based (P5-H).
 *
 *   - Read-only profile (slug, rank, enrollment date, status)
 *   - Eligible rewards chips (if rank in the Connect directory)
 *   - Link to Payment Center for destination config
 *
 * Removed: affiliateType branches, Connection Rewards section, etc.
 */
const AffiliateSettings = ({ affiliate }) => {
  const rank = affiliate?.rank || "none";
  const rankDisplay = affiliate?.rankDisplay || "Unranked";
  const commissionRate = affiliate?.commissionRate ?? 0.2;
  const isRanked = rank !== "none";

  return (
    <div className="space-y-8 p-3 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard/affiliate"
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h2 className="text-2xl font-bold text-white">Affiliate Settings</h2>
          <p className="text-gray-400 text-sm">Your affiliate profile</p>
        </div>
      </div>

      {/* Profile */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-[#7700FF]" />
          Profile
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Rank">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7700FF]/20 text-[#7700FF] text-xs font-bold uppercase">
              <Crown className="w-3 h-3" />
              {rankDisplay}
            </span>
          </Field>
          <Field label="Commission Rate">
            <span className="text-white font-bold">
              {Math.round(commissionRate * 100)}%
            </span>
          </Field>
          <Field label="Share Slug">
            <p className="text-white font-bold font-mono">{affiliate?.shareSlug || "—"}</p>
          </Field>
          <Field label="Status">
            <span
              className={`rounded-full text-xs font-bold px-3 py-1 ${
                affiliate?.status === "active"
                  ? "bg-[#00c853]/20 text-[#00c853]"
                  : affiliate?.status === "suspended"
                  ? "bg-red-500/20 text-red-400"
                  : "bg-[#ffa500]/20 text-[#ffa500]"
              }`}
            >
              {affiliate?.status}
            </span>
          </Field>
          {isRanked && (
            <Field label="Connect Ref ID">
              <p className="text-gray-300 font-mono">{affiliate?.connectRefId || "—"}</p>
            </Field>
          )}
          <Field label="Enrolled Since">
            <p className="text-gray-300">
              {affiliate?.enrolledAt
                ? new Date(affiliate.enrolledAt).toLocaleDateString()
                : "—"}
            </p>
          </Field>
        </div>
      </div>

      {/* Eligible rewards (only for ranked affiliates) */}
      {isRanked && affiliate?.eligibleRewards?.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-[#00c853]" />
            Eligible Rewards
          </h3>
          <p className="text-sm text-gray-400">
            Reward categories your rank qualifies you for via BlockBot Pay.
          </p>
          <div className="flex flex-wrap gap-2">
            {affiliate.eligibleRewards.map((r) => (
              <span
                key={r}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 font-mono font-bold flex items-center gap-1.5"
              >
                <Award className="w-3.5 h-3.5 text-[#00c853]" /> {r}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Payment link */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">Payments</h3>
        <p className="text-sm text-gray-400">
          Manage your payment destination and view history in the Payment Center.
        </p>
        <Link
          to="/dashboard/affiliate/payouts"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7700FF] to-[#00c853] hover:opacity-90 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-opacity"
        >
          Open Payment Center
        </Link>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-500">{label}</label>
    <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">{children}</div>
  </div>
);

export default AffiliateSettings;
